// Sticky red banner shown when Stripe reports `invoice.payment_failed`.
// Reads payment_action_required / payment_action_message from profiles.
// Clicking "Update card" opens the existing Stripe Customer Portal flow.
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { createCustomerPortalSession } from "@/lib/billing.functions";

type PaymentState = {
  required: boolean;
  message: string | null;
};

export function PaymentFailedBanner({ userId }: { userId: string }) {
  const openPortal = useServerFn(createCustomerPortalSession);
  const [state, setState] = useState<PaymentState>({ required: false, message: null });
  const [opening, setOpening] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("payment_action_required, payment_action_message")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled || !data) return;
      setState({
        required: !!(data as any).payment_action_required,
        message: (data as any).payment_action_message ?? null,
      });
    };

    load();

    // Poll every 30s (cheap; column is on profile we already query elsewhere).
    const interval = window.setInterval(load, 30_000);

    // Realtime: react instantly when the webhook flips the flag.
    const channel = supabase
      .channel(`profile-payment-${userId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${userId}` },
        (payload: any) => {
          const row = payload?.new || {};
          setState({
            required: !!row.payment_action_required,
            message: row.payment_action_message ?? null,
          });
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      try { supabase.removeChannel(channel); } catch {}
    };
  }, [userId]);

  const handleUpdateCard = async () => {
    setErr(null);
    setOpening(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error("You're signed out. Please sign in again.");
      const { url } = await openPortal({ data: { accessToken } });
      window.location.href = url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not open billing portal";
      setErr(msg);
      setOpening(false);
    }
  };

  if (!state.required) return null;

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1500,
        background: "#B3261E",
        color: "#FFFFFF",
        padding: "10px 14px",
        margin: "0 -16px 12px -16px",
        fontSize: 13,
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
      role="alert"
    >
      <div style={{ flex: "1 1 auto", minWidth: 0, fontWeight: 500 }}>
        Payment failed — update your card.{" "}
        <span style={{ fontWeight: 400, opacity: 0.95 }}>
          {state.message || "Your last payment didn't go through."}
        </span>
        {err && <div style={{ fontSize: 12, marginTop: 4 }}>{err}</div>}
      </div>
      <button
        onClick={handleUpdateCard}
        disabled={opening}
        style={{
          background: "#FFFFFF",
          color: "#B3261E",
          border: "none",
          borderRadius: 8,
          padding: "6px 14px",
          fontSize: 13,
          fontWeight: 500,
          cursor: opening ? "wait" : "pointer",
          flexShrink: 0,
        }}
      >
        {opening ? "Opening…" : "Update card"}
      </button>
    </div>
  );
}
